<template>
  <section v-if="orderSummaryTotal">
    <div class="row no-gutters">
      <div class="col-12">
        <label>{{ $t('message.my_reservations.summary_total.title') }}</label>
      </div>
    </div>
    <div class="row no-gutters mt-1">
      <div class="col-9">{{ $t('message.my_reservations.summary_total.subtotal') }}</div>
      <div class="col-3 text-right">
        {{ subtotal | currency }}
      </div>
    </div>
    <div class="row no-gutters mt-1" v-if="order.extra">
      <div class="col-9">{{ order.extra | multiLangName }}</div>
      <div class="col-3 text-right">
        {{ orderSummaryTotal.extra_cost | currency }}
      </div>
    </div>
    <div class="row no-gutters mt-1"
         v-if="orderSummaryTotal.promo_code_usage && orderSummaryTotal.promo_code_usage.applied">
      <div class="col-9">
        {{ $t('message.my_reservations.summary_total.discount') }}:
        {{ orderSummaryTotal.promo_code_usage.code }}
        <span v-if="orderSummaryTotal.promo_code_usage.discount_type === 'amount'">
          ({{ orderSummaryTotal.promo_code_usage.discount_amount | currency }})
        </span>
        <span v-else>
          ({{ orderSummaryTotal.promo_code_usage.discount }}%) 
        </span>
      </div>
      <div class="col-3 text-right -status-discount">
        -{{ orderSummaryTotal.discount_cost | currency }}
      </div>
    </div>
    <!-- New cost format (Coins discount after VAT) -->
    <section v-if="order.is_new_cost_format">
      <div class="row no-gutters mt-1" v-if="orderSummaryTotal.service_vat">
        <div class="col-9">{{ $t('message.my_reservations.summary_total.service_vat') }}</div>
        <div class="col-3 text-right">{{ orderSummaryTotal.service_vat | currency }}</div>
      </div>
      <div class="row no-gutters mt-1"
           v-if="orderSummaryTotal.credit_discount_cost">
        <div class="col-9">
          {{ $t('message.my_reservations.summary_total.discount_credits') }}
        </div>
        <div class="col-3 text-right -status-discount">
          -{{ orderSummaryTotal.credit_discount_cost | currency }}
        </div>
      </div>
    </section>
    <!-- Old cost format (Coins discount before VAT) -->
    <section v-else>
      <div class="row no-gutters mt-1"
           v-if="orderSummaryTotal.credit_discount_cost">
        <div class="col-9">
          {{ $t('message.my_reservations.summary_total.discount_credits') }}
        </div>
        <div class="col-3 text-right -status-discount">
          -{{ orderSummaryTotal.credit_discount_cost | currency }}
        </div>
      </div>
      <div class="row no-gutters mt-1" v-if="orderSummaryTotal.service_vat">
        <div class="col-9">{{ $t('message.my_reservations.summary_total.service_vat') }}</div>
        <div class="col-3 text-right">{{ orderSummaryTotal.service_vat | currency }}</div>
      </div>
    </section>

    <div class="row no-gutters mt-3">
      <div class="col-9">
        <label>{{ $t('message.my_reservations.summary_total.total') }}</label>
      </div>
      <div class="col-3 text-right">
        <label>{{ orderSummaryTotal.total | currency }}</label>
      </div>
    </div>
    <div class="row no-gutters" v-if="order.payment_data && order.total_price">
      <div class="col-12">
        <span v-html="$options.filters.orderPaymentData(order.payment_data)"></span>
        <span class="d-inline-block text-orange"
              v-if="!order.paid_status">
          ({{ $t('message.all_pending') }})
        </span>
        <div class="text-center"
             v-if="order.payment_method === 'bank_transfer' && order.bank_transfer_data && order.bank_transfer_data.transfer_picture">
          <div class="py-3">
            <a :href="order.bank_transfer_data.transfer_picture" target="_blank">
              <img :src="order.bank_transfer_data.transfer_picture"
                   alt=""
                   :height="$mq.phone ? 300 : 360"
                   class="rounded border border-gray shadow-sm">
            </a>
          </div>
          <div class="pb-2" v-if="order.cancel === 0">
            <div class="text-small text-secondary text-left">
              {{ $t('message.my_reservations.change_slip_image_remark') }}
            </div>
            <vue-dropzone ref="vueDropzone"
                          id="dropzone"
                          class="dropzone-dashed"
                          :options="dropzoneOptions"
                          @vdropzone-max-files-exceeded="vueDropZoneMaxFilesExceeded"
                          @vdropzone-sending="vueDropZoneAddParams"
                          @vdropzone-mounted="vueDropZoneMounted"
                          @vdropzone-success="vueDropZoneSuccess"/>
          </div>
        </div>
      </div>
    </div>
    <!-- Refund and return coins -->
    <section class="pb-2" v-if="orderSummaryTotal.refund_cost || orderSummaryTotal.returned_credits">
      <hr>
      <div class="row no-gutters pt-1" v-if="orderSummaryTotal.refund_cost">
        <div class="col-9">{{ $t('message.my_reservations.summary_total.cancellation_refund') }}</div>
        <div class="col-3 text-right">{{ orderSummaryTotal.refund_cost | currency }}</div>
      </div>
      <div class="row no-gutters pt-1" v-if="orderSummaryTotal.returned_credits">
        <div class="col-9">{{ $t('message.my_reservations.summary_total.returned_credits') }}</div>
        <div class="col-3 text-right">{{ orderSummaryTotal.returned_credits | currency }}</div>
      </div>
    </section>

    <!-- Transaction date -->
    <div class="row no-gutters mt-1 text-secondary">
      <div class="col-6">
        {{ $t('message.my_reservations.created_at') }}
      </div>
      <div class="col-6 text-right">
        <span v-if="order.created_at">
          {{ order.created_at | prettyDate }}
        </span>
        <span v-else>
          {{ order.order_date | prettyDate }}
        </span>
      </div>
    </div>
    <div class="row no-gutters mt-1 text-secondary" v-if="orderSummaryTotal.reconcile_date">
      <div class="col-6">
        {{ $t('message.my_reservations.paid_at') }}
      </div>
      <div class="col-6 text-right">
        {{ orderSummaryTotal.reconcile_date | prettyDate }}
      </div>
    </div>

    <!-- Order additional hours -->
    <template v-if="displayAdditionalHours()">
      <hr>
      <div class="row no-gutters">
        <div class="col-12">
          <label>{{ $t('message.my_reservations.additional_hours') }}</label>
        </div>
      </div>
      <div v-for="(orderAdditionalHours, index) in order.book_more_hours">
        <div class="d-flex">
          <div class="flex-fill">
            {{ $t('message.my_reservations.additional_hours') }} #{{ orderAdditionalHours.id }}
          </div>
          <div class="flex-fill text-right">
            {{ orderAdditionalHours.total_price | currency }}
          </div>
        </div>

        <div class="d-flex mt-1">
          <div class="flex-fill">
            {{ $t('message.my_reservations.summary_total.service_vat') }}
          </div>
          <div class="flex-fill text-right">
            {{ orderAdditionalHours.service_vat | currency }}
          </div>
        </div>

        <div class="d-flex mt-3">
          <div class="flex-fill">
            <label>{{ $t('message.my_reservations.summary_total.total') }}</label>
          </div>
          <div class="flex-fill text-right">
            <label>{{ Number(orderAdditionalHours.total_price) + Number(orderAdditionalHours.service_vat) | currency }}</label>
          </div>
        </div>

        <div class="d-flex" v-if="orderAdditionalHours.payment_data">
          <div class="flex-fill">
            <span v-html="$options.filters.orderPaymentData(orderAdditionalHours.payment_data)"></span>
            <span class="d-inline-block text-orange" v-if="!order.paid_status">({{ $t('message.all_pending') }})</span>
          </div>
        </div>

        <div class="d-flex mt-1 text-secondary">
          <div class="flex-fill">
            {{ $t('message.my_reservations.created_at') }}
          </div>
          <div class="flex-fill text-right">
            {{ orderAdditionalHours.created_at | prettyDate }}
          </div>
        </div>

        <div class="d-flex mt-1">
          <div class="flex-fill text-secondary">
            {{ $t('message.my_reservations.status') }}
          </div>
          <div class="flex-fill text-right">
            <span :class="getOrderAdditionalHoursStatus(orderAdditionalHours).textColor">
              {{ getOrderAdditionalHoursStatus(orderAdditionalHours).status }}
            </span>
          </div>
        </div>
        <!-- Professional declined request with bank transfer or promptpay -->
        <div class="pt-2"
             v-if="orderAdditionalHours.is_declined && (orderAdditionalHours.payment_method === 'bank_transfer' || orderAdditionalHours.payment_method === 'promptpay')">
          <span class="bullet-notification">
            <i class="fas fa-circle"></i>
          </span>
          <span class="text-secondary">
            {{ $t('message.my_reservations.request_declined_remark') }}
          </span>
        </div>
        <hr v-if="(index + 1) < order.book_more_hours.length">
      </div>
    </template> <!-- End order additional hours -->
  </section>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import vue2Dropzone from 'vue2-dropzone'
  import orderHelpers from '../../../helpers/orderHelpers'

  export default {
    props: {
      order: Object
    },
    components: {
      vueDropzone: vue2Dropzone
    },
    data () {
      return {
        orderSummaryTotal: null,
        subtotal: null,
        textStatusColor: [],
        dropzoneOptions: {
          paramName: 'upload_image',
          acceptedFiles: 'image/*',
          url: 'https://beneat.co',
          thumbnailWidth: 160,
          thumbnailHeight: 160,
          maxFilesize: 5,
          dictDefaultMessage: `<i class="fal fa-cloud-upload"></i> ${this.$i18n.t('message.button.change_slip_image')}`,
          addRemoveLinks: true,
          autoProcessQueue: true,
          maxFiles: 1
        }
      }
    },
    computed: {
      ...mapGetters({
        authState: 'auth/getState',
        globalState: 'globalVal/getState'
      })
    },
    watch: {
      'order.order_summary_total': function (newVal, oldVal) {
        this.orderSummaryTotal = { ...newVal }
        this.subtotal = this.orderSummaryTotal.subtotal
      }
    },
    methods: {
      ...mapActions('orderDetail', ['loadOrderDetail']),
      getOrderAdditionalHoursStatus (orderAdditionalHours) {
        return orderHelpers.getOrderAdditionalHoursStatus(orderAdditionalHours)
      },
      displayAdditionalHours () {
        return this.order.book_more_hours.length > 0
      },
      vueDropZoneMaxFilesExceeded (file) {
        this.$refs.vueDropzone.removeAllFiles()
        this.$refs.vueDropzone.addFile(file)
      },
      vueDropZoneAddParams (file, xhr, formData) {
        formData.append('order_id', this.order.id)
      },
      vueDropZoneMounted () {
        const uploadUrl = `${this.$http.options.root}/bank-transfers/update`
        this.$refs.vueDropzone.options.url = uploadUrl
        this.$refs.vueDropzone.setOption('url', uploadUrl)
        const headers = { 'Authorization': `Bearer ${this.authState.token}` }
        this.$refs.vueDropzone.options.headers = headers
        this.$refs.vueDropzone.setOption('headers', headers)
      },
      async vueDropZoneSuccess (file, response) {
        if (!response.error) {
          await this.loadOrderDetail(this.order.id)
        } else {
          this.$swal({ text: response.message, type: 'error' })
        }
        this.$refs.vueDropzone.removeAllFiles()
      }
    },
    mounted () {
      if (this.order && this.order.order_summary_total) {
        this.orderSummaryTotal = { ...this.order.order_summary_total }
        this.subtotal = this.orderSummaryTotal.subtotal
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/order/components/OrderSummaryTotal.vue